import { Box, Button, CircularProgress, Dialog, IconButton, TextField, Typography } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import Close from "@mui/icons-material/Close";
import ThumbUpOffAltIcon from "@mui/icons-material/ThumbUpOffAlt";
import ThumbDownOffAltIcon from "@mui/icons-material/ThumbDownOffAlt";
import React, { useEffect, useState } from "react";
import { ReactComponent as RotoGPTLogo } from "../../assets/RotoGPTLogo.svg";
import { sendProductFeedback } from "src/api/sendProductFeedback";

type Opinion = "choice 1" | "choice 2";
type FormStep = "opinion" | "nps" | "improve" | "experience" | "thanks";

interface FeedbackFormProps {
  open: boolean;
  accessAuthToken: string | null;
  onClose: () => void;
}

const POPUP_AUTO_CLOSE_DELAY_MS = 2000; // 2 seconds
const NPS_SCORES = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10];

const okButtonSx = {
  color: "#ffffff",
  textTransform: "none" as const,
  fontSize: 18,
  fontWeight: 500,
  mt: 1,
  "&.Mui-disabled": { color: "rgba(255,255,255,0.35)" },
};

const underlineFieldSx = {
  maxWidth: 420,
  width: "100%",
  "& .MuiInput-underline:before": { borderBottomColor: "rgba(255,255,255,0.7)" },
  "& .MuiInput-underline:after": { borderBottomColor: "#ffffff" },
  "& .MuiInputBase-input": { color: "#ffffff", fontSize: 20 },
  "& .MuiInputBase-input::placeholder": { color: "rgba(255,255,255,0.45)", opacity: 1, fontStyle: "italic", fontSize: 20 },
};

const FeedbackForm: React.FC<FeedbackFormProps> = ({ open, accessAuthToken, onClose }) => {
  const [step, setStep] = useState<FormStep>("opinion");
  const [opinion, setOpinion] = useState<Opinion | null>(null);
  const [helpfulness, setHelpfulness] = useState<number | null>(null);
  const [improve, setImprove] = useState("");
  const [experience, setExperience] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    setStep("opinion");
    setOpinion(null);
    setHelpfulness(null);
    setImprove("");
    setExperience("");
    setIsSubmitting(false);
  }, [open]);

  const goBack = () => {
    if (step === "nps") setStep("opinion");
    else if (step === "improve") setStep("nps");
    else if (step === "experience") setStep("improve");
    else onClose();
  };

  const handleSubmit = async () => {
    if (opinion == null || helpfulness == null || !accessAuthToken || isSubmitting) return;
    setIsSubmitting(true);
    try {
      await sendProductFeedback(accessAuthToken, {
        opinion,
        helpfulness,
        improve: improve.trim(),
        experience: experience.trim(),
      });
      setStep("thanks");
      setTimeout(() => onClose(), POPUP_AUTO_CLOSE_DELAY_MS);
    } catch {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      sx={{
        zIndex: 9999,
        "& .MuiDialog-container": { zIndex: 9999 },
      }}
      PaperProps={{
        sx: {
          bgcolor: "#000000",
          color: "#ffffff",
          borderRadius: 2,
          zIndex: 9999,
          boxShadow: "none",
          border: "1px solid #222222",
          display: "flex",
          flexDirection: "column",
          height: 560,
          maxHeight: "calc(100dvh - 72px)",
          overflow: "hidden",
          boxSizing: "border-box",
          // MUI default paper margin is 32px; tighten only on mobile so the form can use the leftover side space
          m: { xs: 1.5, sm: 4 },
          width: { xs: "calc(100% - 24px)", sm: undefined },
        },
      }}
      slotProps={{
        backdrop: {
          sx: {
            position: "fixed",
            backdropFilter: "blur(1px) !important",
            WebkitBackdropFilter: "blur(1px) !important",
            backgroundColor: "rgba(0, 0, 0, 0.35) !important",
          },
        },
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", px: 1, pt: 1, flexShrink: 0, visibility: step === "thanks" ? "hidden" : "visible" }}>
        <IconButton aria-label="back" onClick={goBack} sx={{ color: "#ffffff" }}>
          <ArrowBackIcon />
        </IconButton>
        <IconButton aria-label="close" onClick={onClose} sx={{ color: "#ffffff" }}>
          <Close />
        </IconButton>
      </Box>

      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          px: { xs: 1, sm: 3 },
          pb: 4,
          minHeight: 0,
          gap: 2,
          width: "100%",
          maxWidth: "100%",
          boxSizing: "border-box",
          overflowX: "hidden",
          overflowY: "auto",
        }}
      >
        {step === "opinion" && (
          <>
            <Typography sx={{ textAlign: "center", fontWeight: 500, fontSize: 22, px: 1 }}>
              What do you think of RotoGPT?
            </Typography>
            <Box sx={{ width: "100%", maxWidth: 220, "& svg": { width: "100%", height: "auto", display: "block" }, my: 1 }}>
              <RotoGPTLogo />
            </Box>
            <Box sx={{ display: "flex", gap: 2, width: "100%", maxWidth: 280, minWidth: 0 }}>
              {(["choice 1", "choice 2"] as const).map((choice) => (
                <Button
                  key={choice}
                  onClick={() => setOpinion(choice)}
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    height: 148,
                    borderRadius: 2,
                    bgcolor: "#111111",
                    border: opinion === choice ? "2px solid #ffffff" : "1px solid rgba(255,255,255,0.28)",
                    color: "#ffffff",
                    "&:hover": { bgcolor: "#171717", borderColor: "#ffffff" },
                  }}
                >
                  {choice === "choice 1" ? (
                    <ThumbUpOffAltIcon sx={{ fontSize: 64 }} />
                  ) : (
                    <ThumbDownOffAltIcon sx={{ fontSize: 64 }} />
                  )}
                </Button>
              ))}
            </Box>
            <Button disabled={!opinion} onClick={() => setStep("nps")} sx={okButtonSx}>
              OK
            </Button>
          </>
        )}

        {step === "nps" && (
          <>
            <Typography sx={{ textAlign: "center", fontWeight: 500, fontSize: 20, px: 1, lineHeight: 1.35 }}>
              On a scale of 0 to 10 with 10 being &quot;a lot&quot; and 0 being &quot;not at all&quot;, how much has RotoGPT helped you with your fantasy decision making?*
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: { xs: 1, sm: 1.25 }, width: "100%", minWidth: 0, maxWidth: "100%" }}>
              {[NPS_SCORES.slice(0, 6), NPS_SCORES.slice(6)].map((row, rowIndex) => (
                <Box key={rowIndex} sx={{ display: "flex", justifyContent: "center", flexWrap: "nowrap", gap: { xs: 0.5, sm: 1.25 }, maxWidth: "100%", minWidth: 0 }}>
                  {row.map((score) => (
                    <Button
                      key={score}
                      onClick={() => setHelpfulness(score)}
                      sx={{
                        minWidth: 0,
                        width: { xs: 36, sm: 52 },
                        height: { xs: 40, sm: 48 },
                        px: 0,
                        flexShrink: 0,
                        borderRadius: "999px",
                        color: "#ffffff",
                        bgcolor: "#1a1a1a",
                        border: helpfulness === score ? "2px solid #ffffff" : "1px solid rgba(255,255,255,0.25)",
                        fontSize: { xs: 14, sm: 16 },
                        "&:hover": { borderColor: "#ffffff", bgcolor: "#1a1a1a" },
                      }}
                    >
                      {score}
                    </Button>
                  ))}
                </Box>
              ))}
            </Box>
            <Button disabled={helpfulness == null} onClick={() => setStep("improve")} sx={okButtonSx}>
              OK
            </Button>
          </>
        )}

        {step === "improve" && (
          <>
            <Typography sx={{ textAlign: "center", fontWeight: 500, fontSize: 20, px: 1, lineHeight: 1.35 }}>
              How could we improve the product to earn a 10/10?
            </Typography>
            <Typography sx={{ textAlign: "center", color: "rgba(255,255,255,0.7)", fontSize: 16 }}>
              Any feedback is helpful.
            </Typography>
            <TextField
              fullWidth
              multiline
              minRows={2}
              autoFocus
              variant="standard"
              placeholder="Type your answer here..."
              value={improve}
              onChange={(e) => setImprove(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey && improve.trim()) {
                  e.preventDefault();
                  setStep("experience");
                }
              }}
              sx={underlineFieldSx}
            />
            <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.7)", fontSize: 14 }}>
              Shift + Enter to make a line break
            </Typography>
            <Button disabled={!improve.trim()} onClick={() => setStep("experience")} sx={okButtonSx}>
              OK
            </Button>
          </>
        )}

        {step === "experience" && (
          <>
            <Typography sx={{ textAlign: "center", fontWeight: 500, fontSize: 20, px: 1, lineHeight: 1.35 }}>
              In a sentence or two, describe your experience so far.
            </Typography>
            <Typography sx={{ textAlign: "center", color: "rgba(255,255,255,0.7)", fontSize: 16 }}>
              (or skip by just clicking ok)
            </Typography>
            <TextField
              fullWidth
              multiline
              minRows={2}
              autoFocus
              variant="standard"
              placeholder="Type your answer here..."
              value={experience}
              onChange={(e) => setExperience(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  void handleSubmit();
                }
              }}
              sx={underlineFieldSx}
            />
            <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.7)", fontSize: 14 }}>
              Shift + Enter to make a line break
            </Typography>
            <Button onClick={() => void handleSubmit()} disabled={isSubmitting || !accessAuthToken} sx={okButtonSx}>
              {isSubmitting ? <CircularProgress size={22} sx={{ color: "#ffffff" }} /> : "Submit"}
            </Button>
          </>
        )}

        {step === "thanks" && (
          <>
            <Box sx={{ width: "100%", maxWidth: 220, "& svg": { width: "100%", height: "auto", display: "block" } }}>
              <RotoGPTLogo />
            </Box>
            <Typography sx={{ textAlign: "center", fontWeight: 600, fontSize: 28 }}>
              Thanks for your feedback!
            </Typography>
          </>
        )}
      </Box>
    </Dialog>
  );
};

export default FeedbackForm;
